import {
    AIR_FUNNEL_JOURNEY_TYPE,
    AIR_LOYALTY_USECASE,
    CYS_MAX_CARDS,
    CYS_WIDGET_HEIGHT,
    ELITE_BANNER_HEIGHT_PX,
    WIDGET_MARGIN_HEIGHT,
    WIDGET_STACK_GAP_PX,
    WIDGET_TO_USECASE
} from '@/constants/unifiedWidgetStack.constants'
import type {
    ILoyaltyUhpContentItem,
    IRecentSearchContentItem,
    IUnifiedWidgetConfig,
    IUnifiedWidgetItem,
    IUnifiedWidgetMappedForRender,
    IUnifiedWidgetState,
    IUnifiedWidgetStateUpdate,
    IUnifiedWidgetUsecase,
    IUnifiedWidgetsApiResponseDataType
} from '@/types/unifiedExpandWidgets'

export const hasContentItems = (value: IRecentSearchContentItem[] | undefined) =>
    Boolean(value && Array.isArray(value) && value.length > 0)

const hasLoyaltyContentItems = (
    value: IUnifiedWidgetItem['content'] | undefined
): value is ILoyaltyUhpContentItem[] =>
    Array.isArray(value) &&
    value.length > 0 &&
    typeof (value[0] as ILoyaltyUhpContentItem)?.title === 'string' &&
    typeof (value[0] as ILoyaltyUhpContentItem)?.subtitle === 'string'

export const hasUnifiedWidgetItemUsecaseAndContent = (item: IUnifiedWidgetItem): boolean => {
    if (!item || !item?.usecase) {
        return false
    }
    switch (item.usecase) {
        case AIR_LOYALTY_USECASE:
            return hasLoyaltyContentItems(item.content)
        case AIR_FUNNEL_JOURNEY_TYPE:
            return hasContentItems(item.content as IRecentSearchContentItem[])
        default:
            return false
    }
}

export const getUnifiedWidgetsOnlyUsecasesString = (config: IUnifiedWidgetConfig) => {
    const widgets = config?.widgets || {}
    const usecases: string[] = []
    if (widgets.loyaltyWidget) {
        usecases.push(WIDGET_TO_USECASE.loyalty)
    }
    if (widgets.cysWidget) {
        usecases.push(WIDGET_TO_USECASE.cys)
    }
    return usecases.length > 0 ? usecases.join(',') : ''
}

export const isAnyUnifiedHomeWidgetEnabled = (config: IUnifiedWidgetConfig) => {
    const w = config?.widgets || {}
    return Boolean(w.cysWidget || w.loyaltyWidget)
}

export const getUnifiedWidgetItemMappedForRender = (
    item: IUnifiedWidgetItem
): IUnifiedWidgetMappedForRender | null => {
    if (!hasUnifiedWidgetItemUsecaseAndContent(item)) {
        return null
    }
    switch (item.usecase) {
        case AIR_FUNNEL_JOURNEY_TYPE: {
            const content = item.content as IRecentSearchContentItem[]
            return { usecase: AIR_FUNNEL_JOURNEY_TYPE, content: content?.length > 0 ? content.slice(0, CYS_MAX_CARDS) : [] }
        }
        case AIR_LOYALTY_USECASE: {
            if (!hasLoyaltyContentItems(item.content)) {
                return null
            }
            return { usecase: AIR_LOYALTY_USECASE, loyalty: item.content[0] }
        }
        default:
            return null
    }
}

// Response rows drop the vertical prefix (request AIR_LOYALTY -> row LOYALTY), but content[].type
// keeps it. Re-key each row off content type so we can match on AIR_* everywhere.
const keyRowByContentType = (item: IUnifiedWidgetItem): IUnifiedWidgetItem => {
    const contentType = Array.isArray(item.content)
        ? (item.content[0] as { type?: string })?.type
        : undefined
    return contentType ? { ...item, usecase: contentType } : item
}

export const normalizeUnifiedApiResponse = (
    items: IUnifiedWidgetsApiResponseDataType | null | undefined
): IUnifiedWidgetState => {
    try {
        if (!items?.length) {
            return []
        }
        return items.map(keyRowByContentType).filter(hasUnifiedWidgetItemUsecaseAndContent)
    } catch {
        return []
    }
}

const replaceUnifiedItemContent = (
    prev: IUnifiedWidgetState,
    content: IUnifiedWidgetItem['content'],
    usecase: IUnifiedWidgetUsecase
): IUnifiedWidgetState => {
    const idx = prev?.findIndex(item => item?.usecase === usecase)
    if (idx === -1) {
        return prev
    }
    return prev?.map((item, index) => (index === idx && item ? { ...item, content } : item))
}

export const getUpdatedWidgetState = (
    prev: IUnifiedWidgetState,
    update: IUnifiedWidgetStateUpdate
): IUnifiedWidgetState => {
    if (!update?.usecase || update.usecase !== AIR_FUNNEL_JOURNEY_TYPE) {
        return prev
    }
    if (!hasContentItems(update.content as IRecentSearchContentItem[])) {
        return prev?.filter(item => item?.usecase !== AIR_FUNNEL_JOURNEY_TYPE)
    }
    return replaceUnifiedItemContent(prev, update.content, AIR_FUNNEL_JOURNEY_TYPE)
}

export const combinedHeightFromUnifiedState = (
    unifiedState: IUnifiedWidgetState,
    options: { includeCys: boolean; includeLoyalty: boolean }
): number => {
    if (!unifiedState?.length) {
        return 0
    }
    let visibleWidgetCount = 0
    const widgetsHeight = unifiedState.reduce((sum, item) => {
        if (!hasUnifiedWidgetItemUsecaseAndContent(item)) {
            return sum
        }
        const mapped = getUnifiedWidgetItemMappedForRender(item)
        if (!mapped) {
            return sum
        }
        if (mapped.usecase === AIR_FUNNEL_JOURNEY_TYPE && options.includeCys) {
            visibleWidgetCount += 1
            return sum + CYS_WIDGET_HEIGHT
        }
        if (mapped.usecase === AIR_LOYALTY_USECASE && options.includeLoyalty) {
            visibleWidgetCount += 1
            return sum + ELITE_BANNER_HEIGHT_PX
        }
        return sum
    }, 0)

    if (widgetsHeight <= 0) {
        return 0
    }
    const gapHeight = visibleWidgetCount > 1 ? WIDGET_STACK_GAP_PX * (visibleWidgetCount - 1) : 0
    return widgetsHeight + gapHeight + WIDGET_MARGIN_HEIGHT
}
